import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'core/redux/store';
import { Overview } from 'core/models/overview/overview';
import axios from 'axios';
import { useRef, useState } from 'react';
import { ApiResult } from 'apis/apiresult';
import { Bar } from 'react-chartjs-2';
import BarChart from 'components/chart/chart';
import { BarElement, CategoryScale, Chart, LinearScale, BarController, Tooltip } from 'chart.js';
import { toShamsi, toShamsiDate } from 'utils/scripts/date';
import { Order } from 'core/domains/order';
import { Payment } from 'core/domains/payment';
import { toCurrency } from 'utils/scripts/number';

Chart.register(LinearScale, CategoryScale, BarController, BarElement, Tooltip);

const mapState = (state: RootState) => ({
    state: state,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
    payments: Payment[];
    orders: Order[]
    title: string;
    startDate: string,
    endDate: string
}


const CombineIncomeOverview: React.FC<Props> = (props) => {
    const [chartData, setChartData] = useState<any>({});

    const chartRef = useRef<HTMLCanvasElement | null>(null);

    const combineData = () => {
        const combinedData: any = {};


        // Process orders
        props.orders.forEach(order => {
            var date = new Date(order.createdOn).toISOString().split('T')[0];
            date = toShamsiDate(date); // Get the date part (YYYY-MM-DD)

            if (!combinedData[date]) {
                combinedData[date] = { date, payments: 0, orders: 0 }; // Initialize if not exists
            }
            combinedData[date].orders += order.amount; // Sum orders by date
        });
        // Process payments
        props.payments.forEach(payment => {
            var date = new Date(payment.createdOn).toISOString().split('T')[0];
            date = toShamsiDate(date); // Get the date part (YYYY-MM-DD)
            if (!combinedData[date]) {
                combinedData[date] = { date, payments: 0, orders: 0 }; // Initialize if not exists
            }
            combinedData[date].payments += payment.amount; // Sum payments by date
        });
        // Convert combinedData object to an array
        return Object.values(combinedData);
    };


    React.useEffect(() => {
        const data = combineData();
        var filteredData = data.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        filteredData = filteredData.slice(1);
        // Create the chart
        const ctx = chartRef.current?.getContext('2d');
        if (ctx) {
            const chart = new Chart(ctx, {
                type: 'line',

                data: {
                    labels: filteredData.map((item: any) => item.date),
                    datasets: [
                        {
                            label: 'پرداخت ها',
                            data: data.slice(1).map((item: any) => item.payments),
                            backgroundColor: 'rgba(255, 165, 0,0.6)',
                            borderColor: 'rgba(255, 165, 0,1)',
                            borderWidth: 1,

                        },
                        {
                            label: 'سفارشات',
                            data: data.slice(1).map((item: any) => item.orders),
                            backgroundColor: 'rgba(75, 192, 192, 0.6)',
                            borderColor: 'rgba(75, 192, 192, 1)',
                            borderWidth: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: {
                                font: {
                                    family: 'YekanBakh', // Set font family for legend
                                    size: 14, // Set font size for legend
                                },
                            },
                        },
                        title: {
                            display: true,
                            text: props.title,
                            font: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 16, // Set font size for title
                                weight: 'bold', // Set font weight for title
                            },
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            titleFont: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 14, // Set font size for tooltip title
                            },
                            bodyFont: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 12, // Set font size for tooltip body
                            },
                            callbacks: {
                                // Custom tooltip label
                                title: (tooltipItems) => {
                                    return tooltipItems[0].label; // Show the date as title
                                },
                                label: (tooltipItem) => {
                                    const datasetLabel = tooltipItem.dataset.label || '';
                                    const value = tooltipItem.raw || 0;
                                    return `${datasetLabel}: ${toCurrency(parseInt(value.toString()))}`; // Show amount per line
                                }
                            },
                        },
                    },
                    scales: {
                        x: {
                            stacked: true, // Stack bars
                            title: {
                                display: false,
                                text: 'روز',
                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis title
                                    size: 12,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            },
                            ticks: {
                                display: false,

                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis ticks
                                    size: 10,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            }
                        },
                        y: {
                            stacked: true, // Stack bars
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'تومان',
                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis title
                                    size: 12,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            },
                            ticks: {
                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis ticks
                                    size: 10,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            }
                        },
                    },
                },
            });
            return () => {
                chart.destroy();
            };
        }
        // Cleanup the chart on component unmount
    }, [props.orders, props.payments]); // Re-run effect if payments or orders change


    return (
        <>
            <div className="overview-container">
                <div className="container">
                    {
                        <canvas ref={chartRef} />
                    }
                </div>
            </div>
        </>
    );
};

export default connector(CombineIncomeOverview);
